import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  MessageCircle,
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
} from "lucide-react";
import logoWhite from "../../assets/images/brand/logo-white.png";
import {
  FACTORY_ADDRESS,
  FACTORY_MAP_URL,
  LEGAL_NAME,
  REGISTERED_ADDRESS,
  GSTIN,
} from "../../utils/locations";

const footerLinks = {
  rooms: [
    { name: "Kitchen", href: "/rooms/kitchen" },
    { name: "Living Room", href: "/rooms/living" },
    { name: "Bedroom", href: "/rooms/bedroom" },
    { name: "Wardrobe", href: "/rooms/wardrobe" },
    { name: "Dining", href: "/rooms/dining" },
    { name: "Kids Room", href: "/rooms/kids" },
    { name: "Study", href: "/rooms/study" },
    { name: "Pooja Room", href: "/rooms/pooja" },
  ],
  company: [
    { name: "About Us", href: "/about" },
    { name: "Our Factory", href: "/factory" },
    { name: "Our Process", href: "/process" },
    { name: "Gallery", href: "/gallery" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: "/contact" },
  ],
  services: [
    { name: "For Homeowners", href: "/contact?type=b2c" },
    { name: "For Builders", href: "/contact?type=b2b-builder" },
    { name: "For Architects", href: "/contact?type=b2b-architect" },
    { name: "Doors", href: "/contact?type=doors" },
    { name: "Decors", href: "/contact?type=decors" },
  ],
};

const socialLinks = [
  {
    name: "Instagram",
    icon: Instagram,
    href: "https://www.instagram.com/iravathamhomes/",
  },
  {
    name: "Facebook",
    icon: Facebook,
    href: "https://www.facebook.com/profile.php?id=61584487956713",
  },
  {
    name: "YouTube",
    icon: Youtube,
    href: "https://www.youtube.com/@IravathamHomesInteriorsDecors",
  },
  {
    name: "LinkedIn",
    icon: Linkedin,
    href: "https://www.linkedin.com/company/iravathamhomes/",
  },
];

export default function Footer() {
  return (
    <footer className="bg-brand-primary text-white">
      {/* Main Footer */}
      <div className="container-custom section-padding py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center mb-6">
              <img src={logoWhite} alt="Iravatham" className="h-20 w-auto" />
            </Link>
            <div className="text-white/70 mb-6 max-w-sm leading-relaxed">
              <span className="block text-white font-medium">{LEGAL_NAME}</span>
              <span className="block">Registered Office: {REGISTERED_ADDRESS}</span>
              <span className="block">GSTIN: {GSTIN}</span>
            </div>
            <div className="space-y-3">
              {[
                { label: "Factory", address: FACTORY_ADDRESS, mapUrl: FACTORY_MAP_URL },
              ].map((location) => (
                <div
                  key={location.label}
                  className="flex items-start gap-3 text-white/70"
                >
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="block text-white font-medium">
                      {location.label}
                    </span>
                    <span className="block">{location.address}</span>
                    <a
                      href={location.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-1 text-sm text-brand-accent hover:text-white transition-colors"
                    >
                      Open in Maps →
                    </a>
                  </div>
                </div>
              ))}
              <a
                href="https://wa.me/917995659797?text=Hi, I'm interested in Iravatham Interiors. Please share more details."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/70 hover:text-white transition-colors"
              >
                <MessageCircle className="w-5 h-5 flex-shrink-0" />
                <span>WhatsApp: +91 799 565 9797</span>
              </a>
            </div>
          </div>

          {/* Rooms Column */}
          <div>
            <h4 className="font-heading text-lg mb-5">Design by Room</h4>
            <ul className="space-y-3">
              {footerLinks.rooms.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="font-heading text-lg mb-5">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="font-heading text-lg mb-5">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom section-padding py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-sm">
              © {new Date().getFullYear()} {LEGAL_NAME}. All rights reserved.
            </p>
            <p className="text-white/60 text-sm">
              Made with ❤️ by{" "}
              <a
                href="https://talecrafters.in"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white transition-colors underline"
              >
                TaleCrafters
              </a>
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:bg-white/20 hover:text-white transition-all"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
